import { gql } from '@apollo/client'
import { PRODUCT_FRAGMENT } from '../fragments/product'
import {
  CONSUMER_CART_FRAGMENT,
  CONSUMER_CART_ERRORS_FRAGMENT,
  CONSUMER_CART_WARNINGS_FRAGMENT
} from '../fragments/cart'

const MUTATE_CREATE_CART_CONSUMER = gql`
  mutation createCart(
    $customerDetails: CustomerDetailsParams
    $deliveryAddress: DeliveryAddressParams
    $fulfillmentDate: Date!
    $fulfillmentTime: HourMinute
    $fulfillmentType: FulfillmentType!
    $storeId: UUID!
    $tableNumber: String
    $addressId: UUID
  ) {
    createCart(
      customerDetails: $customerDetails
      deliveryAddress: $deliveryAddress
      fulfillmentDate: $fulfillmentDate
      fulfillmentTime: $fulfillmentTime
      fulfillmentType: $fulfillmentType
      storeId: $storeId
      tableNumber: $tableNumber
      addressId: $addressId
    ) {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }

    }
  }
`

/** Consumer API get cart query */
const QUERY_GET_CART_CONSUMER = gql`
  query cart {
    cart {
      ${CONSUMER_CART_FRAGMENT}
    }
  }
`

/** Consumer API update cart mutation */
const MUTATE_UPDATE_CART_CONSUMER = gql`
  mutation updateCart(
        $customerDetails: CustomerDetailsParams
        $dropoffNotes: String
        $customFieldValue: String
        $giftWrapMessage: String
        $marketingOptIn: Boolean
        $orderNotes: String 
        $saveAddress: Boolean
        $tipValue: Decimal
        $recipientDetails: RecipientDetailsParams
    ) {
    updateCart(
        customerDetails: $customerDetails
        dropoffNotes: $dropoffNotes
        customFieldValue: $customFieldValue
        giftWrapMessage: $giftWrapMessage
        marketingOptIn: $marketingOptIn
        orderNotes: $orderNotes
        saveAddress: $saveAddress
        tipValue: $tipValue
        recipientDetails: $recipientDetails
    ) {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
  }
`

/** Consumer API update cart fulfillment mutation */
const MUTATE_UPDATE_FULFILLMENT_CART_CONSUMER = gql`
  mutation updateCartFulfillment(
        $deliveryAddress: DeliveryAddressParams
        $fulfillmentDate: Date
        $fulfillmentTime: HourMinute
        $fulfillmentType: FulfillmentType
        $proceedWithNewPrices: Boolean
        $storeId: UUID
        $tableNumber: String 
    ) {
    updateCartFulfillment(
        deliveryAddress: $deliveryAddress
        fulfillmentDate: $fulfillmentDate
        fulfillmentTime: $fulfillmentTime
        fulfillmentType: $fulfillmentType
        proceedWithNewPrices: $proceedWithNewPrices
        storeId: $storeId
        tableNumber: $tableNumber
    ) {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
  }
`

const MUTATE_APPLY_DISCOUNT_CART_CONSUMER = gql`
  mutation applyDiscount(
        $discountCode: String!
    ) {
    applyDiscount(
        discountCode: $discountCode
    ) {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
  }
`

const MUTATE_REMOVE_DISCOUNT_CART_CONSUMER = gql`
  mutation removeDiscount {
    removeDiscount {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
  }
`

const MUTATE_APPLY_REWARD_CART_CONSUMER = gql`
  mutation applyReward(
        $rewardId: UUID!
    ) {
    applyReward(
        rewardId: $rewardId
    ) {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
  }
`

const QUERY_VALIDATE_CART_CONSUMER = gql`
  query validateCart {
    validateCart {
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
  }
`

const MUTATE_CREATE_ORDER_ITEM_CONSUMER = gql`
  mutation createOrderItem(
    $modifiers: [OrderItemModifierParams!]
    $quantity: Int!
    $variantId: UUID!
    $proceedWithNewOrderItems: Boolean
    $source: OrderItemSource
  ) {
    createOrderItem(modifiers: $modifiers, quantity: $quantity, variantId: $variantId, proceedWithNewOrderItems: $proceedWithNewOrderItems, source: $source) 
    {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
  }`

const MUTATE_REMOVE_ORDER_ITEMS_CONSUMER = gql`
  mutation removeOrderItems(
    $ids: [UUID]!
  ) {
    removeOrderItems(ids: $ids) 
    {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
}`

const MUTATE_UPDATE_ORDER_ITEM_QUANTITY_CONSUMER = gql`
  mutation updateOrderItemQuantity(
    $id: UUID!
    $quantity: Int!
    $proceedWithNewOrderItems: Boolean
  ) {
    updateOrderItemQuantity(id: $id, quantity: $quantity, proceedWithNewOrderItems: $proceedWithNewOrderItems) 
    {
      cart {
        ${CONSUMER_CART_FRAGMENT}
      }
      errors {
        ${CONSUMER_CART_ERRORS_FRAGMENT}
      }
      warnings {
        ${CONSUMER_CART_WARNINGS_FRAGMENT}
      }
    }
}`

const MUTATE_REMOVE_CART = gql`
  mutation removeCart {
    removeCart
  }
`

const QUERY_GET_RECOMMENDED_PRODUCTS = gql`
  query GetRecommendedProducts {
    cart {
      recommendedProducts {
        ${PRODUCT_FRAGMENT}    
      }
    }
  }
`

export {
  MUTATE_APPLY_DISCOUNT_CART_CONSUMER,
  MUTATE_APPLY_REWARD_CART_CONSUMER,
  MUTATE_CREATE_CART_CONSUMER,
  MUTATE_CREATE_ORDER_ITEM_CONSUMER,
  MUTATE_REMOVE_CART,
  MUTATE_REMOVE_DISCOUNT_CART_CONSUMER,
  MUTATE_REMOVE_ORDER_ITEMS_CONSUMER,
  MUTATE_UPDATE_CART_CONSUMER,
  MUTATE_UPDATE_FULFILLMENT_CART_CONSUMER,
  MUTATE_UPDATE_ORDER_ITEM_QUANTITY_CONSUMER,
  QUERY_GET_CART_CONSUMER,
  QUERY_VALIDATE_CART_CONSUMER,
  QUERY_GET_RECOMMENDED_PRODUCTS
}
